import { GlobalStore, GlobalState } from '@highlight/ipc'

interface RemoteWhitelist {
  domains: string[]
  apps: string[]
  lastFetched: number
}

export interface SupportedAppOrDomain {
  name: string
  type: 'app' | 'domain'
  enabled: boolean
  isRemote: boolean
  isUserModified: boolean
}

export const addToInsightsWhitelist = (name: string, type: 'app' | 'domain') => {
  const normalizedName = name.toLowerCase()
  const key = type === 'app' ? 'insights.whitelistApps' : 'insights.whitelistDomains'
  const currentWhitelist = GlobalStore.settings().get<string[]>(key, []) ?? []

  if (!currentWhitelist.includes(normalizedName)) {
    const updatedWhitelist = [...currentWhitelist, normalizedName]
    GlobalStore.settings().set(key, updatedWhitelist)
  }

  // Remove from blocklist if it exists there
  const blocklistKey = type === 'app' ? 'insights.blocklistApps' : 'insights.blocklistDomains'
  const currentBlocklist = GlobalStore.settings().get<string[]>(blocklistKey, []) ?? []
  if (currentBlocklist.includes(normalizedName)) {
    const updatedBlocklist = currentBlocklist.filter((item) => item !== normalizedName)
    GlobalStore.settings().set(blocklistKey, updatedBlocklist)
  }
}

export const removeFromInsightsWhitelist = (name: string, type: 'app' | 'domain') => {
  const normalizedName = name.toLowerCase()
  const key = type === 'app' ? 'insights.whitelistApps' : 'insights.whitelistDomains'
  const currentWhitelist = GlobalStore.settings().get<string[]>(key, []) ?? []

  if (currentWhitelist.includes(normalizedName)) {
    const updatedWhitelist = currentWhitelist.filter((item) => item !== normalizedName)
    GlobalStore.settings().set(key, updatedWhitelist)
  }

  // Add to blocklist to remember user preference against remote defaults
  const blocklistKey = type === 'app' ? 'insights.blocklistApps' : 'insights.blocklistDomains'
  const currentBlocklist = GlobalStore.settings().get<string[]>(blocklistKey, []) ?? []
  if (!currentBlocklist.includes(normalizedName)) {
    const updatedBlocklist = [...currentBlocklist, normalizedName]
    GlobalStore.settings().set(blocklistKey, updatedBlocklist)
  }
}

export const isInsightsDetectionEnabledForApp = (appName: string): boolean => {
  const normalizedName = appName.toLowerCase()
  const userWhitelist = GlobalStore.settings().get<string[]>('insights.whitelistApps', []) ?? []
  const userBlocklist = GlobalStore.settings().get<string[]>('insights.blocklistApps', []) ?? []
  const remoteWhitelist = GlobalState.get<RemoteWhitelist | null>('insights.remoteWhitelist', null)

  // If user explicitly blocked it, it's disabled (user preference overrides remote)
  if (userBlocklist.includes(normalizedName)) {
    return false
  }

  // If user explicitly whitelisted it, it's enabled
  if (userWhitelist.includes(normalizedName)) {
    return true
  }

  // Check if it's in the remote whitelist
  if (remoteWhitelist && remoteWhitelist.apps.map((app) => app.toLowerCase()).includes(normalizedName)) {
    return true
  }

  return false
}

export const isInsightDetectionEnabledForDomain = (domain: string): boolean => {
  const normalizedDomain = domain.toLowerCase()
  const userWhitelist = GlobalStore.settings().get<string[]>('insights.whitelistDomains', []) ?? []
  const userBlocklist = GlobalStore.settings().get<string[]>('insights.blocklistDomains', []) ?? []
  const remoteWhitelist = GlobalState.get<RemoteWhitelist | null>('insights.remoteWhitelist', null)

  // If user explicitly blocked it, it's disabled (user preference overrides remote)
  if (userBlocklist.includes(normalizedDomain)) {
    return false
  }

  // If user explicitly whitelisted it, it's enabled
  if (userWhitelist.includes(normalizedDomain)) {
    return true
  }

  // Check if it's in the remote whitelist
  if (remoteWhitelist && remoteWhitelist.domains.map((d) => d.toLowerCase()).includes(normalizedDomain)) {
    return true
  }

  return false
}

export const mergeRemoteListWithUserPreferences = (
  remoteApps: string[],
  remoteDomains: string[],
): { supportedApps: SupportedAppOrDomain[]; supportedDomains: SupportedAppOrDomain[] } => {
  const userWhitelistApps = GlobalStore.settings().get<string[]>('insights.whitelistApps', []) ?? []
  const userBlocklistApps = GlobalStore.settings().get<string[]>('insights.blocklistApps', []) ?? []
  const userWhitelistDomains = GlobalStore.settings().get<string[]>('insights.whitelistDomains', []) ?? []
  const userBlocklistDomains = GlobalStore.settings().get<string[]>('insights.blocklistDomains', []) ?? []

  // Process apps
  const allApps = new Set([...remoteApps.map((app) => app.toLowerCase()), ...userWhitelistApps])
  const supportedApps: SupportedAppOrDomain[] = Array.from(allApps)
    .filter((app) => !userBlocklistApps.includes(app)) // Filter out blocked apps
    .map((app) => {
      const isRemote = remoteApps.map((a) => a.toLowerCase()).includes(app)
      const isUserWhitelisted = userWhitelistApps.includes(app)
      return {
        name: app,
        type: 'app' as const,
        enabled: true,
        isRemote,
        isUserModified: isUserWhitelisted && !isRemote,
      }
    })

  // Process domains
  const allDomains = new Set([...remoteDomains.map((domain) => domain.toLowerCase()), ...userWhitelistDomains])
  const supportedDomains: SupportedAppOrDomain[] = Array.from(allDomains)
    .filter((domain) => !userBlocklistDomains.includes(domain)) // Filter out blocked domains
    .map((domain) => {
      const isRemote = remoteDomains.map((d) => d.toLowerCase()).includes(domain)
      const isUserWhitelisted = userWhitelistDomains.includes(domain)
      return {
        name: domain,
        type: 'domain' as const,
        enabled: true,
        isRemote,
        isUserModified: isUserWhitelisted && !isRemote,
      }
    })

  return { supportedApps, supportedDomains }
}

export const getInsightsSupportedItems = (): SupportedAppOrDomain[] => {
  const remoteWhitelist = GlobalState.get<RemoteWhitelist | null>('insights.remoteWhitelist', null)

  if (!remoteWhitelist) {
    // If no remote data, just return user-added items
    const userWhitelistApps = GlobalStore.settings().get<string[]>('insights.whitelistApps', []) ?? []
    const userWhitelistDomains = GlobalStore.settings().get<string[]>('insights.whitelistDomains', []) ?? []

    const userApps: SupportedAppOrDomain[] = userWhitelistApps.map((app) => ({
      name: app,
      type: 'app' as const,
      enabled: true,
      isRemote: false,
      isUserModified: true,
    }))

    const userDomains: SupportedAppOrDomain[] = userWhitelistDomains.map((domain) => ({
      name: domain,
      type: 'domain' as const,
      enabled: true,
      isRemote: false,
      isUserModified: true,
    }))

    return [...userApps, ...userDomains]
  }

  const { supportedApps, supportedDomains } = mergeRemoteListWithUserPreferences(
    remoteWhitelist.apps,
    remoteWhitelist.domains,
  )

  return [...supportedApps, ...supportedDomains]
}

export const isInsightsEnabledForAppOrDomain = (name: string, type: 'app' | 'domain'): boolean => {
  if (type === 'app') {
    return isInsightsDetectionEnabledForApp(name)
  } else {
    return isInsightDetectionEnabledForDomain(name)
  }
}

export const resetInsightsPreferences = () => {
  GlobalStore.settings().set('insights.whitelistApps', [], true)
  GlobalStore.settings().set('insights.whitelistDomains', [], true)
  GlobalStore.settings().set('insights.blocklistApps', [], true)
  GlobalStore.settings().set('insights.blocklistDomains', [], true)
}
